import React from "react";

type PulsingNodeProps = {
  children: React.ReactNode;
  /** Pause between pulses, in ms. Default 2000. */
  interval?: number;
  /** Stops the pulse. */
  isDisabled?: boolean;
  /** Length of a pulse, in ms. Default 1000. */
  duration?: number;
};

/** Draws attention to its child with a copy that swells and fades out, again and again. */

export const PulsingNode: React.FC<PulsingNodeProps> = ({
  children,
  interval = 2000,
  duration = 1000,
  isDisabled
}) => {
  const [pulses, setPulses] = React.useState<number[]>([]);
  const pulseId = React.useRef(0);

  React.useEffect(() => {
    if (isDisabled) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;

    const pulse = () => {
      const newId = pulseId.current++;
      setPulses((prev) => [...prev, newId]);
      timeoutId = setTimeout(() => {
        setPulses((prev) => prev.filter((id) => id !== newId));
        timeoutId = setTimeout(pulse, interval);
      }, duration); // duration of pulse animation
    };

    pulse();

    return () => clearTimeout(timeoutId);
  }, [interval, duration, isDisabled]);

  return (
    <div className="relative inline-block">
      {children}

      {pulses.map((id) => (
        // Decorative copies: read and focused once, through the original
        <div
          key={id}
          aria-hidden
          inert
          className="component-pulse-zoom pointer-events-none absolute inset-0"
        >
          {children}
        </div>
      ))}
    </div>
  );
};
